import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './sampleIn.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { useAuthStore } from '../../auth/store';
import { useNavPermission } from '../../hooks/useNavPermission';

// ─────────────────────────────────────────────────────────────────────────────
// Shared Barcode Sample Custody scan form (Sample Out / Sample Disposal).
// Same prototype chrome as Sample In — top+bottom action bars, amber Barcode
// Scan band, blue "Barcode Transaction" header, the working grid — driven by a
// per-form config so Out and Disposal are thin wrappers.
// (CLAUDE.md §18.11b — legacy SampleInOutController.)
// ─────────────────────────────────────────────────────────────────────────────

const REQ_FIELD: CSSProperties = {
  border: '1px solid #ff8c42', borderRadius: 4, padding: '8px 10px',
  fontSize: 13, background: '#fffbe7', color: 'var(--ai-ink)',
};
const SECTION_HDR: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
  fontWeight: 800, fontSize: 13, letterSpacing: '.3px', color: '#234b70',
  background: 'linear-gradient(180deg,#eaf2fb,#d7e6f6)', borderBottom: '1px solid #b9cbe0',
};

/** One scanned custody line. */
export interface CustodyRow {
  id:             string;
  bottle:         string;
  from:           string;
  to:             string;
  scannedAt:      string;   // ISO — display; server stamps the real timestamp (or the looked-up DateHandled for Location)
  by:             string;
  haz:            boolean;
  mode:           string;
  deplete:        boolean;
  wasteDrum:      string;
  storage?:       string;   // Sample Location
  user?:          string;   // Sample Location
  // save payload
  sampleLoginOid: string | null;
  jobOid:         string | null;
  client:         string | null;
}

/** Shape of the resolve endpoints' `data` (superset across In/Out/Disposal/Location). */
export interface CustodyResolveDto {
  ok:             boolean;
  error:          string | null;
  sampleBottleId: string | null;
  jobId?:         string | null;
  client?:        string | null;
  sampleLoginOid?: string | null;
  jobOid?:        string | null;
  from?:          string | null;
  hazardous?:     boolean;
  storage?:       string | null;   // Location
  user?:          string | null;   // Location
  mode?:          string | null;   // Location
  dateHandled?:   string | null;   // Location
  handledByName?: string | null;   // Location
}

export interface CustodyFormConfig {
  pageId:         string;   // navData id (drives FormTitle icon/colour)
  titleOverride?: string;   // heading text override (e.g. "Sample Disposal History")
  breadcrumbLeaf: string;   // "Sample Out"
  subtitle:       string;   // e.g. "Scan bottle barcodes to check them out"
  actionLabel:    string;   // "Save" | "Dispose"
  confirmTitle:   string;   // "Save" | "Dispose"
  confirmMessage: string;
  successVerb:    string;   // "Saved" | "Disposed"
  formName:       string;   // audit form name
  columns?:       AppColumn<CustodyRow>[];   // static columns (or use buildColumns)
  resolveUrl:     string;
  saveUrl?:       string;   // omitted for read-only (Sample Location)
  emptyPrompt:    string;   // grid empty text
  /** Read-only "scan to find" form (Sample Location / Disposal History): no Save, no Unlink. */
  readOnly?:      boolean;
  /** Route (e.g. "/m/sample-disposal-history") for the top-left History button. */
  historyRoute?:  string;
  buildRow:       (r: CustodyResolveDto, ctx: { userName: string; nowIso: string }) => CustodyRow;
  buildScan?:     (row: CustodyRow) => Record<string, unknown>;
  /** Multi-row scan (Disposal History): one scan returns MANY rows. Overrides buildRow. */
  buildRows?:     (r: CustodyResolveDto, ctx: { userName: string; nowIso: string }) => CustodyRow[];
  /** Editable/interactive columns (e.g. the Deplete checkbox) — given a row updater. */
  buildColumns?:  (u: { updateRow: (id: string, patch: Partial<CustodyRow>) => void }) => AppColumn<CustodyRow>[];
}

export default function CustodyScanForm({ config }: { config: CustodyFormConfig }) {
  const [barcode,  setBarcode]  = useState('');
  const [rows,     setRows]     = useState<CustodyRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [loadMs,   setLoadMs]   = useState<number | null>(null);
  const [loading,  setLoading]  = useState(true);

  const barcodeRef = useRef<HTMLInputElement>(null);
  const notify   = useNotify();
  const runSave  = useSaveFlow();
  const confirm  = useConfirm();
  const navigate = useNavigate();
  const userName = useAuthStore(s => s.user?.name ?? '');
  // MAC write gate (ALPACA parity): Save is enabled only when the user's role is
  // granted Write on this form's NavigationItem (config.formName === the ALPACA
  // NavigationId). Server enforces the same on POST (§14b.5). Read-only forms
  // never render Save, so the resolved read right is harmless there.
  const perm = useNavPermission(config.formName);
  const canWrite = perm.canWrite || perm.isAdmin;

  // In-place row patch (e.g. the Deplete checkbox toggling a row's mode).
  const updateRow = useCallback((id: string, patch: Partial<CustodyRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }, []);
  const columns = useMemo(
    () => (config.buildColumns ? config.buildColumns({ updateRow }) : (config.columns ?? [])),
    [config, updateRow]);

  // No async load, but keep the top load-time chip for parity with the other forms.
  useEffect(() => {
    const t0 = performance.now();
    setLoadMs(performance.now() - t0);
    setLoading(false);
    setTimeout(() => barcodeRef.current?.focus(), 50);
  }, []);

  // Auto-fires when the barcode field commits (blur / Enter / scanner terminator),
  // NOT only on Enter — matching ALPACA's ImmediatePostData editor. The busy guard
  // stops a blur + Enter double-add.
  const scanBusy = useRef(false);
  const scan = useCallback(async () => {
    const code = barcode.trim();
    if (!code || scanBusy.current) return;
    if (rows.some(r => r.bottle.toUpperCase() === code.toUpperCase())) {
      notify.error("This 'Bottle ID' exists already. Please enter a new 'Bottle ID'.");
      setBarcode('');
      return;
    }
    scanBusy.current = true;
    setScanning(true);
    try {
      const res = await api.post(config.resolveUrl, { bottleId: code });
      const r = (res.data as { data: CustodyResolveDto }).data;
      if (!r.ok) { notify.error(r.error ?? "Please enter a valid 'Bottle ID'."); setBarcode(''); return; }
      const ctx = { userName, nowIso: new Date().toISOString() };
      const added = config.buildRows ? config.buildRows(r, ctx) : [config.buildRow(r, ctx)];
      setRows(prev => [...added, ...prev]);
      setBarcode('');
    } catch {
      notify.error('Scan failed. Please try again.');
    } finally {
      scanBusy.current = false;
      setScanning(false);
      barcodeRef.current?.focus();
    }
  }, [barcode, rows, userName, notify, config]);

  const unlink = useCallback(async () => {
    if (selected.size === 0) { notify.warning('Select a scanned row to unlink.'); return; }
    const ok = await confirm.confirm({
      title: 'Unlink', message: `Remove ${selected.size} scanned bottle(s) from this batch?`, tone: 'danger',
    });
    if (!ok) return;
    setRows(prev => prev.filter(r => !selected.has(r.id)));
    setSelected(new Set());
    notify.success("Sample 'Bottle ID' unlinked successfully.");
  }, [selected, confirm, notify]);

  const save = useCallback(() => {
    if (!config.saveUrl || !config.buildScan) return;   // read-only (Sample Location)
    if (rows.length === 0) { notify.error("Please scan the sample 'Bottle ID'."); return; }
    const saveUrl = config.saveUrl;
    const buildScan = config.buildScan;
    setSaving(true);
    runSave({
      formName: config.formName,
      auditAction: 'create',
      confirmTitle: config.confirmTitle,
      confirmMessage: config.confirmMessage,
      save: async () => {
        await api.post(saveUrl, { scans: rows.map(buildScan) });
        setRows([]);
        setSelected(new Set());
      },
      successMessage: `${config.successVerb} ${rows.length} bottle(s).`,
    }).finally(() => setSaving(false));
  }, [rows, runSave, notify, config]);

  const cancel = useCallback(async () => {
    if (rows.length > 0) {
      const ok = await confirm.confirm({ title: 'Cancel', message: `Discard ${rows.length} scanned bottle(s)?`, tone: 'danger' });
      if (!ok) return;
    }
    setRows([]); setSelected(new Set()); setBarcode('');
  }, [rows, confirm]);

  const exportData = useMemo(
    () => ({ filename: config.pageId, ...gridExportMatrix(rows, columns) }),
    [rows, columns, config.pageId]);

  const renderActionBar = (bottom?: boolean) => (
    <div className="bl-card" style={{
      display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
      padding: '10px 12px', marginBottom: bottom ? 0 : 8, marginTop: bottom ? 8 : 0,
    }}>
      {config.historyRoute && (
        <button className="ca-btn" onClick={() => navigate(config.historyRoute!)} title="Disposal History">
          <span className="tb-i">🕘</span> History
        </button>
      )}
      <span style={{ flex: 1 }} />
      {!config.readOnly && (
        <button className="ca-btn ca-btn-solid" onClick={save}
          disabled={saving || rows.length === 0 || !canWrite}
          title={!canWrite ? `You are not authorized to save on ${config.formName}.` : config.actionLabel}>
          {saving ? <><LoaderSymbol size={13} /> {config.successVerb}…</> : <><span className="tb-i">💾</span> {config.actionLabel}</>}
        </button>
      )}
      <button className="ca-btn" onClick={() => void cancel()} disabled={saving} title={config.readOnly ? 'Clear' : 'Cancel'}>
        <span className="tb-i" style={{ color: 'var(--ai-blue, #185fa5)' }}>↩</span> {config.readOnly ? 'Clear' : 'Cancel'}
      </button>
    </div>
  );

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Sample Tracking</span><i className="ti ti-chevron-right" />
            <span>Barcode Sample Custody</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{config.breadcrumbLeaf}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId={config.pageId} title={config.titleOverride} loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">{config.subtitle} · {rows.length} scanned</div>
        </div>
      </div>

      <div className="bl-grid-area">
        {renderActionBar()}

        {/* Barcode Scan band (no To picker for Out / Disposal). */}
        <div className="bl-card" style={{ padding: 12, marginBottom: 8 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Barcode Scan</span>
            <input
              ref={barcodeRef}
              type="text"
              style={{ width: 380, maxWidth: '100%', flex: 'none', ...REQ_FIELD }}
              placeholder="Scan or type a bottle barcode…"
              value={barcode}
              disabled={scanning}
              onChange={e => setBarcode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void scan(); } }}
              onBlur={() => { if (barcode.trim()) void scan(); }}
            />
            {scanning && <LoaderSymbol size={14} />}
          </label>
        </div>

        <div className="bl-card bl-card-flex" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={SECTION_HDR}>
            <span style={{ color: '#f97316' }}>📦</span> Barcode Transaction
            <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#234b70' }}>Count = {rows.length}</span>
          </div>
          <div style={{ padding: 10 }}>
            <div style={{ marginBottom: 8 }}>
              <AppToolbar
                left={config.readOnly ? undefined :
                  <button className="ca-btn" onClick={() => void unlink()} disabled={selected.size === 0} title="Unlink selected">
                    <span className="tb-i">🔗</span> Unlink{selected.size > 0 ? ` (${selected.size})` : ''}
                  </button>
                }
                exportData={exportData}
              />
            </div>
            <AppDataGrid<CustodyRow>
              rows={rows}
              columns={columns}
              getRowId={r => r.id}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText={config.emptyPrompt}
            />
          </div>
        </div>

        {renderActionBar(true)}
      </div>
    </div>
  );
}
